import { useState } from "react";
import { api, formatApiError, uploadFiles } from "@/lib/api";
import { toast } from "sonner";
import { X, Check, Undo2, Loader2 } from "lucide-react";
import { usePhotoInputs, PhotoUploadButton } from "@/components/shared/PhotoPicker";
import { SignedImg } from "@/components/shared/SignedImg";
import { DefectCrop } from "@/components/defects/DefectCrop";

// Sekce „Závady“ v detailu úkolu — samostatná tabulka task_defects (routes/defects.py).
// Komentáře úkolu zůstávají na běžnou řeč, závady mají vlastní stav a fotky opravy.
export const STATUS = {
  draft: ["nová", "bg-secondary text-muted-foreground"],
  open: ["k opravě", "bg-destructive/10 text-destructive"],
  fixed: ["opraveno", "bg-amber-500/15 text-amber-700 dark:text-amber-400"],
  accepted: ["převzato", "bg-success/15 text-success"],
};
export const nz = (n) => (n === 1 ? "závada" : n >= 2 && n <= 4 ? "závady" : "závad");

// 189: pořadí fotek v prohlížeči úkolu — za každou fotkou „před“ hned fotky opravy
// jejích závad, ať se mezi před a po jen přetáhne prstem.
export const beforeAfterSequence = (photos, defects) =>
  photos.flatMap((u) => [u, ...defects.filter((d) => d.photo_url === u).flatMap((d) => d.fix_photos || [])])
    .filter((u, k, a) => a.indexOf(u) === k);

function Chip({ status, round }) {
  const [label, cls] = STATUS[status] || STATUS.open;
  return <span className={`shrink-0 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10.5px] font-extrabold uppercase tracking-wide ${cls}`}>{label}{round > 1 && status !== "accepted" ? " · znovu" : ""}</span>;
}

// Tlačítko „Opraveno · vyfotit“ — hook fotek se volá po řádcích, proto komponenta.
function FixButton({ taskId, defect, onDone }) {
  const [busy, setBusy] = useState(false);
  const pick = usePhotoInputs({
    disabled: busy,
    onChange: async (e) => {
      const files = [...(e.target.files || [])]; e.target.value = "";
      if (!files.length) return;
      setBusy(true);
      try {
        // 257: fotky souběžně s opakováním; jedna nepovedená nezahodí ostatní
        const { urls, failed } = await uploadFiles(files.slice(0, 5));
        if (!urls.length) throw failed[0]?.error || new Error("upload");
        await api.post(`/tasks/${taskId}/defects/${defect.id}/fix`, { photos: urls });
        toast.success(`Závada ${defect.n} opravena.`, failed.length ? { description: `${failed.length} z ${urls.length + failed.length} fotek se nenahrálo — přidej je znovu.` } : undefined); onDone?.();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Nahrání fotky selhalo."); }
      finally { setBusy(false); }
    },
  });
  return (
    <>
      <PhotoUploadButton onClick={pick.open} busy={busy} label="Opraveno · vyfotit" testid={`defect-fix-${defect.n}`} className="min-h-[46px] py-2 text-[14px]" />
      {pick.inputs}
    </>
  );
}

export function TaskDefects({ task, defects, drafts, setDrafts, canDecide, canFix, onShow, onShowDraft, onChanged, onZoom }) {
  const [busy, setBusy] = useState(null);
  if (!defects.length && !drafts.length) return null;
  const accepted = defects.filter((d) => d.status === "accepted").length;
  const decide = async (d, accept) => {
    setBusy(d.id);
    try {
      await api.post(`/tasks/${task.id}/defects/${d.id}/${accept ? "accept" : "reject"}`);
      toast.success(accept ? `Závada ${d.n} převzata.` : `Závada ${d.n} jde znovu k opravě.`); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-card border border-border rounded-[14px] p-4" data-testid="task-defects">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="text-[15px] font-bold">Závady <span className="text-muted-foreground font-semibold tabular-nums">· {defects.length + drafts.length}</span></h3>
        {defects.length > 0 && <span className="text-[12.5px] font-bold text-muted-foreground tabular-nums">{accepted} z {defects.length} převzato</span>}
      </div>

      <div className="divide-y divide-border">
        {defects.map((d) => (
          <div key={d.id} className="grid grid-cols-[56px_1fr] gap-3 py-3" data-testid={`defect-${d.n}`}>
            <DefectCrop url={d.photo_url} x={d.x} y={d.y} n={d.n} tone={d.status} onClick={() => onShow(d)} />
            <div className="min-w-0 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <b className="text-[13.5px]">Závada {d.n}</b>
                <Chip status={d.status} round={d.round} />
              </div>
              <p className="text-sm leading-snug break-words">{d.text}</p>
              {(d.fix_photos || []).length > 0 && (
                <div className="grid grid-cols-2 gap-2 max-w-[340px]">
                  <div><div className="text-[10px] font-bold uppercase tracking-[.06em] text-muted-foreground mb-1">před</div>
                    <DefectCrop url={d.photo_url} x={d.x} y={d.y} n={d.n} tone="open" size="100%" zoom={2.4} className="!w-full aspect-[4/3] !h-auto" onClick={() => onShow(d)} /></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[.06em] text-muted-foreground mb-1">po opravě</div>
                    <button type="button" onClick={() => onZoom?.(d.fix_photos[0])} data-testid={`defect-after-${d.n}`} className="relative block w-full aspect-[4/3] rounded-[12px] overflow-hidden border border-border">
                      <SignedImg url={d.fix_photos[0]} alt="" thumb={240} className="w-full h-full object-cover" />
                      {d.fix_photos.length > 1 && <span className="absolute right-1.5 bottom-1.5 rounded-full bg-black/60 text-white text-[11px] font-extrabold px-2 py-0.5 tabular-nums">+{d.fix_photos.length - 1}</span>}
                    </button></div>
                </div>
              )}
              {canFix && d.status === "open" && <FixButton taskId={task.id} defect={d} onDone={onChanged} />}
              {canDecide && d.status === "fixed" && (
                <div className="grid grid-cols-2 gap-2 max-w-[340px]">
                  <button type="button" disabled={busy === d.id} onClick={() => decide(d, true)} data-testid={`defect-accept-${d.n}`}
                    className="h-11 rounded-[12px] bg-success hover:bg-success/90 text-white font-extrabold text-[13.5px] inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                    {busy === d.id ? <Loader2 size={15} className="animate-spin motion-reduce:animate-none" /> : <Check size={16} />} Převzít</button>
                  <button type="button" disabled={busy === d.id} onClick={() => decide(d, false)} data-testid={`defect-reject-${d.n}`}
                    className="h-11 rounded-[12px] bg-secondary hover:bg-secondary/80 font-extrabold text-[13.5px] inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                    <Undo2 size={15} /> Není opraveno</button>
                </div>
              )}
            </div>
          </div>
        ))}
        {drafts.map((d, k) => (
          <div key={`d${k}`} className="grid grid-cols-[56px_1fr] gap-3 py-3" data-testid={`defect-draft-${k}`}>
            <DefectCrop url={d.photo_url} x={d.x} y={d.y} n={defects.length + k + 1} tone="draft" onClick={() => onShowDraft(k)} />
            <div className="min-w-0 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <b className="text-[13.5px]">Závada {defects.length + k + 1}</b>
                <span className="flex items-center gap-1.5"><Chip status="draft" />
                  <button type="button" onClick={() => setDrafts((ds) => ds.filter((_, i) => i !== k))} aria-label="Odebrat závadu"
                    className="w-7 h-7 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary"><X size={15} /></button></span>
              </div>
              {d.text.trim()
                ? <p className="text-sm leading-snug break-words">{d.text}</p>
                : <button type="button" onClick={() => onShowDraft(k)} className="text-sm font-bold text-brand text-left">Doplnit popis…</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
